import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react';
import { Badge } from '@components/ui/badge';
import { EmployeeDto } from '../../core/api/CorporateClient';
import { useTabs } from '../../components/hooks/use-tabs';
import { TenantCorporateContext } from '..';
import { useWrapRequest } from '../../components/hooks/use-wrap-request';
import { PageHeader } from '../../components/page-header';
import { PageTabs } from '../../components/page-tabs';
import { Spinner } from '../../components/spinner';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Facilities', id: 'facilities' },
    { label: 'Activities', id: 'activities' },
    { label: 'Activity Points', id: 'activity-points' }
];

export const EmployeeContext = React.createContext<EmployeeDto>(
    {} as EmployeeDto
);

export default observer(() => {
    const { pathname } = useLocation();
    const { tab, getPageLink, setPageTab } = useTabs(pageTabs);
    const navigate = useNavigate();
    const corporateClient = React.useContext(TenantCorporateContext);

    const { employeeId } = useParams();

    const employeeRequest = useWrapRequest(
        async () => {
            const result =
                await corporateClient.support.employeeSupportControllerGetEmployee(
                    Number(employeeId)
                );

            return { ...result.data, content: result.data || [] };
        },
        {
            deps: []
        }
    );

    const changePage = (page: number) => {
        navigate(getPageLink(page));
    };

    React.useEffect(() => {
        setPageTab(pathname, 5);
    }, [pathname]);

    return (
        <>
            {employeeRequest.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: (employee) => (
                    <EmployeeContext.Provider value={employee}>
                        <PageHeader
                            title={`${employee.firstName} ${employee.lastName}`}
                            badges={
                                employee.email && (
                                    <Badge variant="outline">
                                        {employee.email}
                                    </Badge>
                                )
                            }
                        />
                        <PageTabs
                            tabs={pageTabs}
                            value={tab}
                            onChange={changePage}
                        />
                        <div className="mt-6">
                            <Outlet />
                        </div>
                    </EmployeeContext.Provider>
                )
            })}
        </>
    );
});
